import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as EmptyContactsStateIcon } from '@ubeya/shared-web/assets/empty-state-employee.svg';
import {
  ConfirmationModal,
  FlexColumn,
  FlexColumnCenter,
  Header3,
  PaginationTable
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { editable, media } from '@ubeya/shared-web/style';
import { API_DATE_TIME_FORMAT, SLOT_FIELDS } from '@ubeya/shared/constants';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import useSuppliers from '@ubeya/shared/hooks/account/useSuppliers';
import useJobTypes from '@ubeya/shared/hooks/generic/useJobTypes';
import {
  SORT_BY_BOOLEAN,
  SORT_BY_BREAKS_DURATION,
  SORT_BY_LOCATION_NAME,
  SORT_BY_NUMBER
} from '@ubeya/shared/utils/sorting';
import moment from 'moment';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import useAdmins from '@ubeya/shared/hooks/account/useAdmins';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import { OSEmployeeCard } from 'shared/components';
import { PAYROLL_CUSTOM_FIELD_TYPE } from 'shared/constants/payroll';
import ExternalApproveRenderer from '../../../timeSheet/components/CellRenderers/ExternalApproveRenderer';
import ActionsRenderer from './ActionsRenderer';
import EditableWageRenderer from './EditableWageRenderer';
import RatingRenderer from './RatingRenderer';
import ExternalNameRenderer from './ExternalNameRenderer';
import { FieldRenderer } from 'payroll/components/CellRenderers';
import TotalClientRateRenderer from 'payroll/components/CellRenderers/TotalClientRateRenderer';
import {
  AreaRenderer,
  BreaksRenderer,
  CheckInTimeRenderer,
  CheckOutTimeRenderer,
  ClientSignoffRenderer,
  InTimeRenderer,
  LockedTimesheetRenderer,
  OutTimeRenderer,
  PayrollFieldTotalAggregateRenderer,
  PositionRenderer,
  ShiftHoursRenderer,
  TotalTimeRenderer,
  TotalTimeWithoutBreaksRenderer
} from 'timeSheet/components/CellRenderers';
import NotesRenderer from 'timeSheet/components/CellRenderers/NotesRenderer';
import ShiftDetails from 'timeSheet/components/ShiftDetails/ShiftDetails';
import AdminApprovalRenderer from 'timeSheet/components/CellRenderers/AdminApprovalRenderer';
import BlockEmployeeModal from 'timeSheet/components/CellRenderers/BlockEmployeeModal';

const EmptyStateWrapper = styled(FlexColumnCenter)`
  flex: 1;
  height: 100%;

  > svg {
    height: 200px;
  }
`;

const EmptyStateTitle = styled(Header3)`
  margin: 30px 0;
`;

const Wrapper = styled(FlexColumn)`
  flex: 1;
  min-height: 90vh;
  ${media.mobile`min-height: unset;`}
`;

const StyledPaginationTable = styled(PaginationTable)`
  .row {
    ${editable}
  }
  .header.approve {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .client-signoff-image {
    height: 100%;
    width: 50%;
  }
`;

const EmptyState = () => {
  const { t } = useContext(ConfigContext);

  return (
    <EmptyStateWrapper>
      <EmptyContactsStateIcon />

      <EmptyStateTitle>{t('noExternalEmployees')}</EmptyStateTitle>
    </EmptyStateWrapper>
  );
};

const MAX_PAGE_SIZE = 100;

const OrderTimeSheetTable = ({
  data: rawData,
  sort,
  accountId,
  editProjectOSSlot,
  editOSTimeSheet,
  updateOSStartTime,
  updateOSEndTime,
  updateOSCheckInTime,
  updateOSCheckOutTime,
  updateOSEmployee,
  updateOSReviewOnEmployee,
  updateOSBreaks,
  deleteOSBreak,
  updateOSShiftDetails,
  fields,
  setSort,
  mappedEmployeesClientStatus,
  projectId,
  orderId: currOrderId,
  allowClientBillingInfo = false,
  showDateColumn = false,
  showCheckInOutColumns = false
}) => {
  const { t } = useContext(ConfigContext);
  const { config: { enableRulesEngineFeature } = {} } = useAccount();
  const { dateAndTime } = useFormattingMap();
  const [selectedEmployee, setSelectedEmployee] = useState();
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [page, setPage] = useState(1);

  const { mappedLabels } = useLabels();
  const { mappedJobTypes } = useJobTypes();
  const { mappedLocations } = useLocations();
  const { mappedSupplierBranches } = useSuppliers();

  const { mappedAdmins } = useAdmins();

  // This is pre-pagination data
  const initData = useMemo(() => (rawData ? (rawData.length === 1 && !!rawData[0]?.summarized ? [] : rawData) : []), [
    rawData
  ]);

  // This is post-pagination data
  const data = useMemo(() => {
    if (initData?.length > MAX_PAGE_SIZE) {
      const { summarized, nonSummarized } = initData.reduce(
        (acc, item) => {
          if (item.summarized) {
            acc.summarized = item;
            return acc;
          }

          acc.nonSummarized.push(item);
          return acc;
        },
        { summarized: null, nonSummarized: [] }
      );
      const startIndex = (page - 1) * MAX_PAGE_SIZE;
      const paginated = nonSummarized.slice(startIndex, startIndex + MAX_PAGE_SIZE);

      return summarized ? [...paginated, summarized] : paginated;
    }
    return initData;
  }, [initData, page]);

  const sortByAreaName = useCallback((a, b) => SORT_BY_LOCATION_NAME(a, b, mappedLocations), [mappedLocations]);

  const [shiftModal, setShiftModal] = useState(false);
  const [blockModal, setBlockModal] = useState(false);

  const { mappedSupplierPositions } = useSuppliers();
  const { mappedPositions } = usePositions();

  // all entries in the table share the same supplier
  const currentBranch = mappedSupplierBranches[rawData?.[0]?.supplierBranchId];
  const enableBlockClients = !!currentBranch?.config?.allowClientToBlockEmployees;

  const columns = useMemo(
    () => [
      {
        Header: t('timeApproval'),
        dataKey: 'approve',
        className: 'approve',
        Cell: ExternalApproveRenderer,
        columnData: {
          editOSTimeSheet,
          mappedSupplierBranches
        },
        maxWidth: 150,
        minWidth: 150,
        sortable: false,
        captureRowClick: false,
        resizable: false,
        cellWrapperProps: { style: { padding: '2px', direction: 'ltr' } },
        cellRendererProps: {
          style: {
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }
        },
        frozen: true
      },
      {
        Header: t('timesheetLocked'),
        dataKey: 'isLocked',
        Cell: LockedTimesheetRenderer,
        sortFunc: SORT_BY_BOOLEAN('isLocked'),
        frozen: true,
        minWidth: 80,
        maxWidth: 80,
        resizable: false,
        captureRowClick: false,
        category: t('approval'),
        cellRendererProps: {
          style: { margin: 'auto' }
        }
      },
      showDateColumn
        ? {
            Header: t('shiftDate'),
            dataKey: 'date',
            formatter: (value) => <>{value}</>,
            category: t('basic')
          }
        : undefined,
      {
        Header: t('name'),
        dataKey: 'name',
        Cell: ExternalNameRenderer,
        minWidth: 200,
        cellRendererProps: {
          enabled: false,
          style: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            position: 'relative'
          }
        },
        columnData: {
          onChangeRating: editProjectOSSlot,
          onClick: setSelectedEmployee,
          t,
          mappedEmployeesClientStatus,
          updateOSEmployee
        }
      },
      {
        Header: t('jobRating'),
        dataKey: 'rating',
        Cell: RatingRenderer,
        columnData: {
          onChangeRating: editProjectOSSlot,
          mappedEmployeesClientStatus,
          enableBlockClients
        },
        minWidth: 120,
        maxWidth: 120
      },
      {
        Header: t('in'),
        dataKey: 'in',
        Cell: InTimeRenderer,
        columnData: {
          updateOSStartTime,
          updateOSEndTime,
          onOutsideClickSave: true,
          allowUpdateExternal: true,
          setIsPromptOpen
        },
        width: 120,
        category: t('basic')
      },
      ...(showCheckInOutColumns
        ? [
            {
              Header: t('checkInTime'),
              dataKey: 'checkIn',
              Cell: CheckInTimeRenderer,
              columnData: {
                updateOSCheckInTime,
                updateOSCheckOutTime,
                onOutsideClickSave: true,
                allowUpdateExternal: true
              },
              minwidth: 120,
              category: t('basic')
            },
            {
              Header: t('checkOutTime'),
              dataKey: 'checkOut',
              Cell: CheckOutTimeRenderer,
              columnData: {
                updateOSCheckOutTime,
                onOutsideClickSave: true,
                allowUpdateExternal: true
              },
              minwidth: 120,
              category: t('basic')
            }
          ]
        : []),
      {
        Header: t('out'),
        dataKey: 'out',
        Cell: OutTimeRenderer,
        columnData: { updateOSEndTime, onOutsideClickSave: true, allowUpdateExternal: true, setIsPromptOpen },
        width: 120,
        category: t('basic')
      },
      ...(enableRulesEngineFeature
        ? [
            {
              Header: t('paidHours'),
              dataKey: 'paidHours',
              formatter: (value) => {
                if (value == null) return '';
                const hours = Math.floor(value);
                const mins = Math.round((value - hours) * 60);
                return `${hours < 10 ? `0${hours}` : hours}:${mins < 10 ? `0${mins}` : mins}`;
              },
              width: 100,
              category: t('basic')
            }
          ]
        : []),
      {
        Header: t('totalTime'),
        dataKey: 'total',
        Cell: TotalTimeRenderer,
        width: 110
      },
      {
        Header: t('totalTimeWithoutBreaks'),
        dataKey: 'totalWithoutBreaks',
        Cell: TotalTimeWithoutBreaksRenderer,
        width: 110,
        category: t('basic')
      },
      ...(allowClientBillingInfo
        ? [
            {
              Header: t('clientWage'),
              dataKey: 'clientWage',
              Cell: EditableWageRenderer,
              columnData: {
                parent: 'clientWage',
                wageKey: 'hourlyWage',
                withPrefix: true
              }
            },
            {
              Header: t('clientMarkup'),
              dataKey: 'clientWageMarkup',
              Cell: EditableWageRenderer,
              columnData: {
                parent: 'clientWage',
                wageKey: 'markup',
                withPrefix: true,
                hideZero: true
              },
              maxWidth: 100,
              category: t('payroll'),
              sortable: false
            },
            {
              Header: t('totalCost'),
              dataKey: 'clientWageTotal',
              Cell: TotalClientRateRenderer,
              sortFunc: SORT_BY_NUMBER,
              category: t('payroll')
            }
          ]
        : []),

      {
        Header: t('supplierPosition'),
        dataKey: 'positionId',
        Cell: PositionRenderer,
        width: 150,
        columnData: { showShiftHours: false, showDuration: false, mappedPositions: mappedSupplierPositions },
        category: t('basic')
      },
      {
        Header: t('position'),
        dataKey: 'clientPositionId',
        Cell: PositionRenderer,
        width: 150,
        columnData: { showShiftHours: false, showDuration: false, mappedPositions, key: 'clientPositionId' },
        category: t('basic')
      },

      {
        Header: t('shift'),
        dataKey: 'shift',
        Cell: ShiftHoursRenderer,
        columnData: { accountId, mappedLabels, mappedJobTypes, mappedLocations },
        sortable: false,
        category: t('basic')
      },
      {
        Header: t('shiftArea'),
        dataKey: 'clientLocationId',
        Cell: AreaRenderer,
        columnData: { mappedLocations, key: 'clientLocationId' },
        flexGrow: 2.5,
        sortFunc: sortByAreaName,
        category: t('basic')
      },
      {
        Header: t('reviewOnEmployee'),
        dataKey: 'review',
        Cell: NotesRenderer,
        columnData: {
          onUpdate: updateOSReviewOnEmployee,
          key: SLOT_FIELDS.reviewOnEmployee,
          enablePreviewModal: true,
          isSingleRow: true
        },
        minWidth: 160,
        sortable: false,
        category: t('basic')
      },
      {
        Header: t('breaks'),
        dataKey: 'breaks',
        maxWidth: 100,
        Cell: BreaksRenderer,
        columnData: { updateOSBreaks, deleteOSBreak },
        sortable: true,
        sortFunc: SORT_BY_BREAKS_DURATION('breaktimes'),
        category: t('basic'),
        visible: false
      },

      {
        Header: t('schedulingNotes'),
        dataKey: 'notes',
        formatter: (value) => value,
        sortable: false,
        category: t('basic'),
        visible: false
      },

      {
        Header: t('approvedBy'),
        dataKey: 'clientApproved.adminId',
        Cell: ({ rowData }) => (
          <AdminApprovalRenderer rowData={{ ...rowData, isExternal: true }} columnData={{ mappedAdmins }} />
        ),
        width: 80,
        minWidth: 50,
        sortable: false,
        captureRowClick: false,
        category: t('approval')
      },
      {
        Header: t('approvedAt'),
        dataKey: `clientApproved.createdAt`,
        formatter: (value) => (value ? dateAndTime(moment(value).format(API_DATE_TIME_FORMAT)) : ''),
        width: 80,
        minWidth: 50,
        sortable: false,
        captureRowClick: false,
        category: t('approval')
      },
      {
        Header: t('clientSignoff'),
        dataKey: 'clientApproved',
        Cell: ClientSignoffRenderer,
        sortable: false,
        cellRendererProps: { style: { height: '100%', width: '100%' } },
        cellWrapperProps: { style: { padding: '2px' } },
        category: t('approval'),
        width: 200
      },

      ...(allowClientBillingInfo
        ? [
            ...(fields || []).map(({ slug, id, isGeneric, type }) => ({
              Header: isGeneric ? t(slug) : slug,
              dataKey: `fields.${id}`,
              Cell: ({ rowData }) =>
                rowData.summarized ? (
                  <PayrollFieldTotalAggregateRenderer
                    rowData={rowData}
                    columnData={{ fieldId: id, readOnly: true, type }}
                  />
                ) : (
                  <FieldRenderer rowData={rowData} columnData={{ fieldId: id, readOnly: true, type }} />
                ),
              maxWidth: 100,
              sortable: false,
              category: t('payroll'),
              visible: false
            })),
            ...(fields || [])
              .filter(({ type }) => type === PAYROLL_CUSTOM_FIELD_TYPE.HOURS_RELATED)
              .map(({ slug, id, isGeneric }) => ({
                Header: isGeneric ? t(slug) : `${slug} ${t('total')}`,
                dataKey: `fieldsTotal.${id}`,
                Cell: ({ rowData }) =>
                  rowData.summarized ? (
                    <PayrollFieldTotalAggregateRenderer
                      rowData={rowData}
                      columnData={{ fieldId: id, showTotal: true, readOnly: true }}
                    />
                  ) : (
                    <FieldRenderer rowData={rowData} columnData={{ fieldId: id, showTotal: true, readOnly: true }} />
                  ),
                maxWidth: 100,
                sortable: false,
                category: t('payroll'),
                visible: false
              }))
          ]
        : [])
    ],
    [
      t,
      editOSTimeSheet,
      mappedSupplierBranches,
      showDateColumn,
      editProjectOSSlot,
      mappedEmployeesClientStatus,
      updateOSEmployee,
      enableBlockClients,
      updateOSStartTime,
      updateOSEndTime,
      showCheckInOutColumns,
      updateOSCheckInTime,
      updateOSCheckOutTime,
      enableRulesEngineFeature,
      allowClientBillingInfo,
      mappedSupplierPositions,
      mappedPositions,
      accountId,
      mappedLabels,
      mappedJobTypes,
      mappedLocations,
      sortByAreaName,
      updateOSReviewOnEmployee,
      updateOSBreaks,
      deleteOSBreak,
      fields,
      mappedAdmins,
      dateAndTime
    ]
  );

  const getRowStyle = useCallback(({ summarized }) => (summarized ? 'summarized' : ''), []);

  return (
    <Wrapper>
      <StyledPaginationTable
        page={page}
        setPage={setPage}
        pageSize={MAX_PAGE_SIZE}
        totalItems={initData?.length ? initData.length - 1 : 0} // -1 to exclude the summarized row
        columns={columns}
        resizable
        showColumnsMenu
        headerRowHeight={55}
        isLoading={false}
        autoHeight
        columnsMenuTitle={t('configureOrderTable')}
        storageName={`projects-order-${accountId}-${projectId}-${currOrderId}`}
        isStickyDrawer
        rowStyle={getRowStyle}
        data={data}
        sortBy={sort.sortBy}
        sortDirection={sort.sortDirection}
        setSort={setSort}
        shadow={false}
        sortable
        gridHeight={initData?.length === 0 ? 300 : undefined}
        noDataRenderer={() => <EmptyState />}
        actionRenderer={({ rowData }) => (
          <ActionsRenderer
            rowData={rowData}
            columnData={{
              setBlockModal,
              onClickShift: setShiftModal,
              onlyShiftDetails: true,
              enableBlockClients,
              mappedEmployeesClientStatus
            }}
          />
        )}
      />
      {selectedEmployee && (
        <OSEmployeeCard
          clientProjectId={projectId}
          defaultData={selectedEmployee}
          onClose={() => setSelectedEmployee()}
          refetchPayroll
        />
      )}

      {shiftModal && (
        <ShiftDetails
          shift={shiftModal}
          onClose={() => setShiftModal()}
          updateShiftDetails={updateOSShiftDetails}
          readOnly
          showAuditLogs={false}
        />
      )}

      {blockModal && (
        <BlockEmployeeModal
          onClose={() => setBlockModal(false)}
          handleBlockEmployee={updateOSEmployee}
          data={blockModal}
        />
      )}

      {isPromptOpen && (
        <ConfirmationModal
          onClose={() => setIsPromptOpen(false)}
          title={t('areYouSureYouEnteredTheCorrectTime')}
          body={t('theShiftDurationWillBeHours', { duration: isPromptOpen.duration })}
          onConfirm={() => {
            isPromptOpen.onConfirm?.();
            setIsPromptOpen(false);
          }}
          confirmText={t('ok')}
          showCancelButton
        />
      )}
    </Wrapper>
  );
};

export default OrderTimeSheetTable;
